/* eslint-disable */
import type { Color } from "../../cash/ui/Color.pb";
import type { Image } from "../../cash/ui/Image.pb";
import type { Money } from "../../common/currency.pb";
import type { BlockerAction } from "../blocker_action.pb";

/**
 * A blocker that presents an exchange rate and allows the customer to specify an amount in
 * the native currency or the target currency. Allows the server to specify various tiers of
 * exchange rate and fees.
 * Will be used by both Remittances and Cross-border payments
 */
export interface MultiCurrencyAmountEntryBlocker {
  /** optional ("Mexico [flag-mx unicode sequence]") */
  header?: string;
  /** required (Money(amount = 200_00, currency_code = USD)), refers to the initiator */
  sender_amount?: Money;
  /** optional ("You send" (or null for x-border)) */
  sender_label?: string;
  /** required (false for remittances, true for x-border) */
  show_arrows?: boolean;
  /** required (Money(amount = 4128_78, currency_code = MXN)), refers to the respondent */
  receiver_amount?: Money;
  /** optional ("They get" (or null for x-border)) */
  receiver_label?: string;
  /** required (defaults to SENDER) */
  focused_field?: MultiCurrencyAmountEntryBlockerFocusedCurrencyField;
  /** required ("Exchange rate") */
  exchange_rate_label?: string;
  cost_tiers?: MultiCurrencyAmountEntryBlockerCostTier[];
  /** required, the action to take when submitting a valid set of amounts */
  primary_action?: BlockerAction;
  /** optional */
  secondary_action?: BlockerAction;
  /** optional, used to display the initiator's selected instrument */
  instrument_token?: string;
  /** optional, used to display the respondent's avatar information in the header */
  recipient?: MultiCurrencyAmountEntryBlockerCustomerDetails;
  /** required, a switcher for displaying Remittance or X-Border elements */
  amount_entry_type?: MultiCurrencyAmountEntryBlockerAmountEntryType;
  /** optional, a description of the transaction provided by the initiator */
  note_prefill_value?: string;
}

export enum MultiCurrencyAmountEntryBlockerFocusedCurrencyField {
  SENDER = "SENDER",
  RECEIVER = "RECEIVER",
}

export enum MultiCurrencyAmountEntryBlockerAmountEntryType {
  REMITTANCE = "REMITTANCE",
  CROSS_BORDER = "CROSS_BORDER",
}

export interface MultiCurrencyAmountEntryBlockerCostTier {
  /** required (opaque token) */
  identifier?: string;
  /** required (Money(amount = 0, currency_code = USD)) */
  min?: Money;
  /** required (Money(amount = 999, currency_code = USD)) */
  max?: Money;
  /** required ("19.4608") */
  customer_exchange_rate?: string;
  /** what fees apply at this cost tier */
  fees?: MultiCurrencyAmountEntryBlockerCostTierFee[];
  /**
   * if set, the next button should instead display a modal with this title
   * ("You must send at least $10")
   */
  error_message_title?: string;
  /** if set, the modal will add this for context ("because reasons…") */
  error_message_details?: string;
}

export interface MultiCurrencyAmountEntryBlockerCostTierFee {
  /** required ("Service fee") */
  label?: string;
  /** required (Money(amount = 199, currency_code = USD)) */
  amount?: Money;
  /**
   * required (default false)
   * If true, the client should deduct this fee before applying the customer_exchange_rate
   * and display the effective total exchange rate on this screen.
   * If false, the client should present the exchange rate as-is and the fee as an additional
   * cost to the sender.
   */
  blend?: boolean;
  /** required, used by the server to confirm fees were displayed properly */
  identifier?: string;
}

export interface MultiCurrencyAmountEntryBlockerCustomerDetails {
  full_name?: string;
  email_address?: string;
  sms_number?: string;
  photo?: Image;
  /** used as the avatar placeholder background if photo is missing */
  themed_accent_color?: Color;
  /** the Franklin identifier used for customers, e.g. C_abcd12345 */
  customer_token?: string;
}
